import { useToast } from '~/components/ui/toast/use-toast'
import { useApiCall } from '~/composables/useApi'
import { useApiPost } from '~/composables/useApiPost'
import type { TNewsCard } from '~/components/entity/news/card'

interface FavoriteResponse {
  status: string
  message: string
  data: unknown
}

export const useFavorites = () => {
  const { isLoggedIn, getToken, isFavorite, addFavoriteId, removeFavoriteId } = useUser()
  const authModalStore = useAuthModalStore()
  const { toast } = useToast()
  const { t } = useI18n()

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Add to favorites
  const addToFavorites = async (newsId: number): Promise<FavoriteResponse | null> => {
    const token = getToken()
    if (!isLoggedIn.value || !token) {
      error.value = t('widget.news_page.details.authorization_required')
      return null
    }

    try {
      isLoading.value = true
      error.value = null

      const { data: response, error: apiError } = await useApiPost<FavoriteResponse>(
        '/add-to-favorites',
        {
          news_id: newsId
        },
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      )

      if (apiError) throw new Error('API Error')

      // Add to user state if successful
      if (response?.status === 'success') {
        addFavoriteId(newsId)

        // Show success toast
        toast({
          title: t('widget.news_page.details.added_to_favorites_title'),
          description: t('widget.news_page.details.added_to_favorites_description'),
          variant: 'default'
        })
      }

      return response
    } catch (err: unknown) {
      console.error('addToFavorites error:', err)
      error.value = (err as Error).message || t('widget.news_page.details.add_to_favorites_error')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Remove from favorites
  const removeFromFavorites = async (newsId: number): Promise<FavoriteResponse | null> => {
    const token = getToken()
    if (!isLoggedIn.value || !token) {
      error.value = t('widget.news_page.details.authorization_required')
      return null
    }

    try {
      isLoading.value = true
      error.value = null

      // Assuming there's a remove endpoint - adjust URL as needed
      const { data: response, error: apiError } = await useApiPost<FavoriteResponse>(
        '/remove-from-favorites',
        {
          news_id: newsId
        },
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      )

      if (apiError) throw new Error('API Error')

      // Remove from user state if successful
      if (response?.status === 'success') {
        removeFavoriteId(newsId)

        // Show success toast
        toast({
          title: t('widget.news_page.details.removed_from_favorites_title'),
          description: t('widget.news_page.details.removed_from_favorites_description'),
          variant: 'default'
        })
      }

      return response
    } catch (err: unknown) {
      error.value =
        (err as Error).message || t('widget.news_page.details.remove_from_favorites_error')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Toggle favorite status
  const toggleFavorite = async (
    newsId: number,
    onSuccess?: () => void
  ): Promise<FavoriteResponse | null> => {
    // Check if user is logged in
    if (!isLoggedIn.value || !getToken()) {
      authModalStore.openLoginModalWithAlert(t('widget.news_page.details.login_required'))
      return null
    }

    const wasInFavorites = isFavorite.value(newsId)
    let result: FavoriteResponse | null = null

    if (wasInFavorites) {
      result = await removeFromFavorites(newsId)
    } else {
      result = await addToFavorites(newsId)
    }

    // Call onSuccess callback if operation was successful
    if (result?.status === 'success' && onSuccess) {
      onSuccess()
    }

    return result
  }

  // Get full favorite news data for favorites page (with pagination)
  const getFavoriteNews = async (page = 1, perPage = 30) => {
    const token = getToken()
    if (!isLoggedIn.value || !token) {
      error.value = t('widget.news_page.details.login_to_system_required')
      return { data: [], meta: null, links: null }
    }

    try {
      isLoading.value = true
      error.value = null

      const response = await useApiCall<{
        data: TNewsCard[]
        meta: {
          current_page: number
          last_page: number
          per_page: number
          total: number
          from: number | null
          to: number | null
        }
        links: {
          first: string | null
          last: string | null
          prev: string | null
          next: string | null
        }
      }>(`/get-favorite-news?page=${page}&per_page=${perPage}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return {
        data: response?.data || [],
        meta: response?.meta || null,
        links: response?.links || null
      }
    } catch (err: unknown) {
      error.value = (err as Error).message || t('widget.news_page.details.get_favorites_error')
      return { data: [], meta: null, links: null }
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    isFavorite,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    getFavoriteNews
  }
}
